import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  variant?: 'light' | 'dark' | 'glass-dark'; // light = white text (for dark bg), dark = dark text on solid white, glass-dark = see-through glass with dark text
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    label, 
    helperText, 
    error, 
    leftIcon, 
    rightIcon, 
    fullWidth = false,
    variant = 'dark',
    className, 
    id, 
    ...props 
  }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substring(2, 9)}`;
    
    const isLight = variant === 'light';
    const isGlassDark = variant === 'glass-dark';

    const labelClass = isLight
      ? 'text-white'
      : isGlassDark
        ? 'text-gray-900'
        : 'text-gray-900';

    const iconClass = isLight
      ? 'text-white/70'
      : isGlassDark
        ? 'text-gray-700'
        : 'text-gray-600';

    const inputClass = isLight
      ? 'autofill-light-text bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/50 focus:ring-2 focus:ring-white/20 focus:border-white/40'
      : isGlassDark
        ? 'autofill-dark-text bg-transparent backdrop-blur-md border border-gray-900/50 text-gray-900 placeholder-gray-700 focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900 shadow-none'
        : 'autofill-dark-text bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500';

    const helperClass = error
      ? 'text-red-600'
      : isLight
        ? 'text-white/70'
        : isGlassDark
          ? 'text-gray-700'
          : 'text-gray-600';

    return (
      <div className={twMerge(fullWidth ? 'w-full' : '', 'mb-4')}>
        {label && (
          <label
            htmlFor={inputId}
            className={twMerge('block text-sm font-medium mb-2', labelClass)}
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className={twMerge(
              'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none',
              iconClass
            )}>
              {leftIcon}
            </div>
          )}

          <input
            id={inputId}
            ref={ref}
            {...props}
            className={twMerge(
              'block rounded-lg shadow-sm transition-all duration-300',
              inputClass,
              leftIcon ? 'pl-10' : 'pl-4',
              rightIcon ? 'pr-10' : 'pr-4',
              'py-2 w-full',
              error ? 'border-red-400 focus:ring-red-400/30 focus:border-red-400' : '',
              className
            )}
            style={{
              // Hard-force the text colour so nothing (autofill, cascade, global
              // color-transition, etc.) can win over the variant's intent.
              ...(isLight
                ? { color: '#ffffff', WebkitTextFillColor: '#ffffff', caretColor: '#ffffff' }
                : { color: '#111827', WebkitTextFillColor: '#111827', caretColor: '#111827' }),
              ...(props.style || {}),
            }}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>

        {(helperText || error) && (
          <p className={twMerge('mt-2 text-sm', helperClass)}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';